import { type MouseEvent, useCallback, useEffect, useRef } from 'react';
import { getChallengeSize } from './getChallengeSize';
import * as Styled from './ThreeDSecureModal.styled';

interface ThreeDSecureModalProps {
  url: string;
  onComplete: (success: boolean) => void;
  onClose: () => void;
}

export const ThreeDSecureModal = ({
  url,
  onComplete,
  onClose,
}: ThreeDSecureModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const urlCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const challengeSize = getChallengeSize(window.innerWidth);

  // Handle escape key to close modal
  const handleEscapeKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    console.log('3DS Modal: Mounting with URL:', url);

    // Add escape key listener
    document.addEventListener('keydown', handleEscapeKey);

    // Listen for messages from the iframe
    const handleMessage = (event: MessageEvent) => {
      console.log('3DS Modal: Message received:', {
        origin: event.origin,
        data: event.data,
      });

      // Validate origin (Opayo domains)
      if (
        event.origin.includes('opayo.eu.elavon.com') ||
        event.origin.includes('sagepay') ||
        event.origin === window.location.origin
      ) {
        // Handle completion messages
        if (
          event.data === '3DS-authentication-complete' ||
          event.data === 'authentication-successful' ||
          event.data.status === 'success' ||
          event.data.type === '3ds-success'
        ) {
          console.log('3DS Modal: Authentication successful');
          onComplete(true);
        } else if (
          event.data === '3DS-authentication-failed' ||
          event.data === 'authentication-failed' ||
          event.data.status === 'failed' ||
          event.data.type === '3ds-failed'
        ) {
          console.log('3DS Modal: Authentication failed');
          onComplete(false);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Monitor iframe URL changes as a fallback
    urlCheckIntervalRef.current = setInterval(() => {
      if (iframeRef.current?.contentWindow) {
        // Try to access the URL (will fail for cross-origin, but worth trying)
        const currentUrl = iframeRef.current.contentWindow.location.href;
        console.log('3DS Modal: Current iframe URL:', currentUrl);

        // Check for success/failure indicators in URL
        if (currentUrl.includes('success') || currentUrl.includes('approved')) {
          console.log('3DS Modal: Success detected in URL');
          onComplete(true);
        } else if (
          currentUrl.includes('failed') ||
          currentUrl.includes('declined')
        ) {
          console.log('3DS Modal: Failure detected in URL');
          onComplete(false);
        }
      }
    }, 1000);

    // Cleanup
    return () => {
      console.log('3DS Modal: Unmounting');
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('message', handleMessage);
      if (urlCheckIntervalRef.current) {
        clearInterval(urlCheckIntervalRef.current);
      }
    };
  }, [url, onComplete, handleEscapeKey]);

  // Handle backdrop click
  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Styled.ModalOverlay onClick={handleBackdropClick}>
      <Styled.ModalContainer $challengeSize={challengeSize}>
        <Styled.ModalHeader>
          <Styled.ModalTitle>Payment Verification</Styled.ModalTitle>
          <Styled.CloseButton onClick={onClose} aria-label="Close verification">
            ×
          </Styled.CloseButton>
        </Styled.ModalHeader>
        <Styled.IframeContainer $challengeSize={challengeSize}>
          <iframe
            ref={iframeRef}
            src={url}
            title="3D Secure Authentication"
            allow="payment"
            sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation"
          />
        </Styled.IframeContainer>
        <Styled.ModalFooter>
          <Styled.InfoText>
            Please complete the verification in the window above. Do not close
            this window.
          </Styled.InfoText>
        </Styled.ModalFooter>
      </Styled.ModalContainer>
    </Styled.ModalOverlay>
  );
};
